import { useState, useEffect, useRef, type PointerEvent } from "react";
import {
  DEFAULT_ACTIVE,
  VISUAL_CONFIG,
} from "@/app/features/diagrams/functions/heroSystem/constants";
import { RadarBackground } from "./RadarBackground";
import { NetworkConnections } from "./NetworkConnections";
import { NetworkNodes } from "./NetworkNodes";
import { SnippetCard } from "./SnippetCard";

export type HeroSystemVisualProps = {
  /** Enable/disable the background concentric grid rotation */
  enableRadarRotation?: boolean;
  /** Speed of clockwise rotation (e.g. '160s') */
  radarSpeedClockwise?: string;
  /** Speed of counter-clockwise rotation (e.g. '110s') */
  radarSpeedCounterClockwise?: string;

  /** Enable/disable the flowing SVG packet dashes */
  enablePacketFlow?: boolean;
  /** Length of the packet dash in SVG units */
  packetDashLength?: number;
  /** Spacing/gap between packets (larger than canvas path length for single packet effect) */
  packetDashGap?: number;
  /** Duration of packet flow animation (e.g. '3.2s') */
  packetFlowDuration?: string;

  /** Enable/disable the idle auto-tour cycling */
  enableAutoTour?: boolean;
  /** Time in ms of inactivity before auto-tour triggers/resumes */
  autoTourIdleDelay?: number;
  /** Interval in ms between cycling node steps */
  autoTourInterval?: number;
};

export function HeroSystemVisual({
  enableRadarRotation = VISUAL_CONFIG.enableRadarRotation,
  radarSpeedClockwise = VISUAL_CONFIG.radarSpeedClockwise,
  radarSpeedCounterClockwise = VISUAL_CONFIG.radarSpeedCounterClockwise,
  enablePacketFlow = VISUAL_CONFIG.enablePacketFlow,
  packetDashLength = VISUAL_CONFIG.packetDashLength,
  packetDashGap = VISUAL_CONFIG.packetDashGap,
  packetFlowDuration = VISUAL_CONFIG.packetFlowDuration,
  enableAutoTour = VISUAL_CONFIG.enableAutoTour,
  autoTourInterval = VISUAL_CONFIG.autoTourInterval,
}: HeroSystemVisualProps) {
  const [selectedNode, setSelectedNode] = useState<string>(DEFAULT_ACTIVE);
  const [activeNode, setActiveNode] = useState<string>(DEFAULT_ACTIVE);
  const [isHoverEnabled, setIsHoverEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(hover: hover)").matches;
    }
    return false;
  });

  const [userInteracted, setUserInteracted] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!isHoverEnabled || isMobile) return;
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left - width / 2;
    const mouseY = e.clientY - rect.top - height / 2;

    const rX = -(mouseY / (height / 2)) * 3; // Max 3 degrees tilt
    const rY = (mouseX / (width / 2)) * 3;

    container.style.transform = `perspective(1000px) rotateX(${rX}deg) rotateY(${rY}deg) scale3d(1.008, 1.008, 1.008)`;
    container.style.transition =
      "transform 0.18s cubic-bezier(0.25, 1, 0.5, 1)";
  };

  const handlePointerLeave = () => {
    const container = containerRef.current;
    if (container) {
      container.style.transform =
        "perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)";
      container.style.transition =
        "transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)";
    }
    handleHoverEnd();
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(hover: hover)");
    const handler = (e: MediaQueryListEvent) => setIsHoverEnabled(e.matches);
    mediaQuery.addEventListener("change", handler);

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      mediaQuery.removeEventListener("change", handler);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Auto-tour idle cycle timer loop
  useEffect(() => {
    if (!enableAutoTour || userInteracted) return;

    const cycleNodesList = isMobile
      ? [
          "center",
          "intelligent-systems",
          "data-search-knowledge",
          "automation-operations",
          "software-architecture-engineering",
        ]
      : [
          "center",
          "intelligent-systems",
          "energy",
          "media-entertainment",
          "data-search-knowledge",
          "agriculture",
          "automation-operations",
          "enterprise",
          "software-architecture-engineering",
          "healthcare",
          "military",
        ];

    const tourTimer = setInterval(() => {
      setActiveNode((prevActive) => {
        const currentIndex = cycleNodesList.indexOf(prevActive);
        const nextIndex = (currentIndex + 1) % cycleNodesList.length;
        const nextNode = cycleNodesList[nextIndex] || prevActive;

        setSelectedNode(nextNode);
        return nextNode;
      });
    }, autoTourInterval);

    return () => {
      clearInterval(tourTimer);
    };
  }, [userInteracted, enableAutoTour, autoTourInterval, isMobile]);

  const resetIdleTimer = () => {
    if (!enableAutoTour) return;
    setUserInteracted(true);
  };

  const handleHoverStart = (nodeId: string) => {
    resetIdleTimer();
    if (!isHoverEnabled) return;
    setActiveNode(nodeId);
  };

  const handleHoverEnd = () => {
    resetIdleTimer();
    setActiveNode(selectedNode);
  };

  const handleClickNode = (nodeId: string) => {
    resetIdleTimer();
    if (selectedNode === nodeId) {
      setSelectedNode(DEFAULT_ACTIVE);
      setActiveNode(DEFAULT_ACTIVE);
    } else {
      setSelectedNode(nodeId);
      setActiveNode(nodeId);
    }
  };

  const isPathActiveForNode = (from: string, to: string, nodeId: string) => {
    if (nodeId === "center") {
      return from === "center" || to === "center";
    }
    return from === nodeId || to === nodeId;
  };

  return (
    <div
      ref={containerRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      className="relative flex flex-col justify-between rounded-shell shadow-ga-shadow border border-ga-line transition-transform duration-300 ease-out bg-ga-paper-strong transform-3d"
    >
      {/* SVG Graphics Container — decorative only; the real link lives in SnippetCard below */}
      <div
        className="w-full aspect-square shrink-0 px-8 pt-6 pb-2 translate-z-[30px] transform-3d"
        aria-hidden="true"
      >
        <svg
          className="w-full h-full select-none"
          viewBox="0 0 200 200"
          fill="none"
          role="presentation"
        >
          <RadarBackground
            enableRadarRotation={enableRadarRotation}
            radarSpeedClockwise={radarSpeedClockwise}
            radarSpeedCounterClockwise={radarSpeedCounterClockwise}
            packetDashLength={packetDashLength}
            packetDashGap={packetDashGap}
            packetFlowDuration={packetFlowDuration}
          />

          <NetworkConnections
            activeNode={activeNode}
            selectedNode={selectedNode}
            isPathActiveForNode={isPathActiveForNode}
            enablePacketFlow={enablePacketFlow}
          />

          <NetworkNodes
            activeNode={activeNode}
            selectedNode={selectedNode}
            handleHoverStart={handleHoverStart}
            handleHoverEnd={handleHoverEnd}
            handleClickNode={handleClickNode}
          />
        </svg>
      </div>

      {/* Editorial context bottom description box */}
      <SnippetCard activeNode={activeNode} />
    </div>
  );
}
